import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { Formik } from 'formik';
import { close } from 'ionicons/icons';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { usePermissionsLazy } from '../../api/authorization.provider';
import {
  CreateScratchpadBucketMutation,
  ScratchpadBucket,
  ScratchpadBucketScope,
  UpdateScratchpadBucketMutation,
  useCreateScratchpadBucketMutation,
  useGetScratchpadBucketQuery,
  useUpdateScratchpadBucketMutation,
} from '../../api/graphql/generated';
import { useCurrentUserProp } from '../../api/user.hooks';
import { useMachineContext } from '../../pages/machine/machine-context-provider';
import { PickerInput } from '../../scratchpad/application/shared/component/widget/picker-input';
import { TextInput } from '../../scratchpad/application/shared/component/widget/text-input';
import { PickerOption } from '../../scratchpad/application/shared/component/widget/widget.types';
import { logger } from '../../util/logger';
import { FormSubmitButton } from '../form/form-submit.button';
import { SaveStatusToast } from '../toast/toast.gql';

interface FormValues {
  name: string | undefined;
  scope: ScratchpadBucketScope | undefined;
}

interface BucketMetadata {
  data?: Record<string, unknown>;
  tags?: Record<string, unknown>;
  type?: string;
}

interface SaveBucketOptions {
  name?: string;
  scope?: ScratchpadBucketScope;
  beforeSave: (values: FormValues) => BucketMetadata;
  afterSave?: (bucket: ScratchpadBucket) => void;
  scratchpadAppSlug: string;
  bucketId?: string;
}

interface ScratchpadBucketSaveModalProps {
  isOpen: boolean;
  onClose: () => void;
  beforeSave: (values: FormValues) => BucketMetadata;
  afterSave?: (bucket: ScratchpadBucket) => void;
  presentingElement?: HTMLElement;
  scratchpadAppSlug: string;
  title?: string;
  bucketId?: string;
}

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  scope: yup.string().required(),
});

export const useScratchpadBucketSave = () => {
  const { machine } = useMachineContext();
  const tenantId = useCurrentUserProp<string>('tenantId');
  const [
    createScratchpadBucket,
    { loading: createLoading, error: createError },
  ] = useCreateScratchpadBucketMutation();
  const [
    updateScratchpadBucket,
    { loading: updateLoading, error: updateError },
  ] = useUpdateScratchpadBucketMutation();

  const getParentId = useCallback(
    (scope: ScratchpadBucketScope): string | undefined => {
      switch (scope) {
        case ScratchpadBucketScope.TEAM:
          return machine?.teamId;
        case ScratchpadBucketScope.TENANT:
          return tenantId;
        case ScratchpadBucketScope.MACHINE:
        default:
          return machine?.id;
      }
    },
    [tenantId, machine?.id, machine?.teamId]
  );

  const saveBucket = useCallback(
    async (options: SaveBucketOptions) => {
      const {
        name,
        scope,
        beforeSave,
        afterSave,
        scratchpadAppSlug,
        bucketId,
      } = options;
      try {
        const values: FormValues = {
          name,
          scope,
        };
        const { data, tags, type } = beforeSave(values);

        let savedBucket: ScratchpadBucket | null | undefined;

        if (bucketId) {
          // For updates, only include name/scope if explicitly provided
          const input = {
            id: bucketId,
            ...(name && { name }),
            ...(scope && { scope, parentId: getParentId(scope) }),
            data,
            tags,
            type,
            scratchpadAppSlug,
          };

          const result = await updateScratchpadBucket({
            variables: { input },
          });
          savedBucket = (result.data as UpdateScratchpadBucketMutation)
            ?.updateScratchpadBucket;
        } else {
          // For new buckets, name and scope are required
          if (!name || !scope) {
            throw new Error('Name and scope are required for new buckets');
          }

          const input = {
            name,
            scope,
            parentId: getParentId(scope),
            data,
            tags,
            type,
            scratchpadAppSlug,
          };

          const result = await createScratchpadBucket({
            variables: { input },
          });
          savedBucket = (result.data as CreateScratchpadBucketMutation)
            ?.createScratchpadBucket;
        }

        if (savedBucket) {
          afterSave?.(savedBucket);
          return savedBucket;
        }
      } catch (error) {
        logger.error('Error saving scratchpad bucket', { error });
        throw error;
      }
    },
    [createScratchpadBucket, updateScratchpadBucket, getParentId]
  );

  return {
    saveBucket,
    saving: createLoading || updateLoading,
    error: createError || updateError,
  };
};

export const useScratchpadBucketSaveModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalProps, setModalProps] = useState<Omit<
    ScratchpadBucketSaveModalProps,
    'isOpen' | 'onClose'
  > | null>(null);

  const { saveBucket, saving, error } = useScratchpadBucketSave();

  const showModal = useCallback(
    (props: Omit<ScratchpadBucketSaveModalProps, 'isOpen' | 'onClose'>) => {
      setModalProps(props);
      setIsOpen(true);
    },
    []
  );

  const hideModal = useCallback(() => {
    setIsOpen(false);
    setModalProps(null);
  }, []);

  const Modal = useCallback(() => {
    if (!modalProps) return null;

    return (
      <ScratchpadBucketSaveModal
        {...modalProps}
        isOpen={isOpen}
        onClose={hideModal}
      />
    );
  }, [isOpen, modalProps, hideModal]);

  return {
    showModal,
    saveBucket,
    Modal,
    saving,
    error,
  };
};

export const ScratchpadBucketSaveModal = (
  props: ScratchpadBucketSaveModalProps
): ReactElement => {
  const {
    isOpen,
    onClose,
    beforeSave,
    afterSave,
    presentingElement,
    scratchpadAppSlug,
    title,
    bucketId,
  } = props;

  const { i18n } = useTranslation();
  const permissionCheck = usePermissionsLazy();
  const tenantId = useCurrentUserProp<string>('tenantId');

  const { data: bucketData } = useGetScratchpadBucketQuery({
    variables: { input: { id: bucketId || '' } },
    skip: !bucketId,
  });

  const { machine } = useMachineContext();

  const bucketScopeOptions = useMemo<PickerOption[]>(() => {
    const options: PickerOption[] = [];

    const teamScopeEnabled = permissionCheck<Partial<ScratchpadBucket>>(
      'scratchpad_bucket',
      {
        parentId: machine?.teamId,
        scope: ScratchpadBucketScope.TEAM,
      }
    );

    const tenantScopeEnabled = permissionCheck<Partial<ScratchpadBucket>>(
      'scratchpad_bucket',
      {
        parentId: tenantId,
        scope: ScratchpadBucketScope.TENANT,
      }
    );

    if (teamScopeEnabled.create) {
      options.push({
        label: i18n.t('common:team'),
        value: ScratchpadBucketScope.TEAM,
      });
    }

    if (tenantScopeEnabled.create) {
      options.push({
        label: i18n.t('common:workspace'),
        value: ScratchpadBucketScope.TENANT,
      });
    }

    // Always add MACHINE scope as default
    options.push({
      label: i18n.t('common:machine'),
      value: ScratchpadBucketScope.MACHINE,
    });

    return options;
  }, [machine?.teamId, tenantId, permissionCheck, i18n]);

  const formInitialValues = useMemo(
    () => ({
      name: bucketData?.getScratchpadBucket?.name || '',
      scope:
        bucketData?.getScratchpadBucket?.scope || ScratchpadBucketScope.MACHINE,
    }),
    [bucketData]
  );

  const { saveBucket, saving, error: saveError } = useScratchpadBucketSave();

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const savedBucket = await saveBucket({
          name: values.name,
          scope: values.scope,
          beforeSave,
          afterSave,
          scratchpadAppSlug,
          bucketId,
        });
        if (savedBucket) {
          onClose();
        }
      } catch (error) {
        logger.error('Error saving scratchpad bucket', { error });
      }
    },
    [saveBucket, beforeSave, afterSave, scratchpadAppSlug, bucketId, onClose]
  );

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
    >
      <IonContent>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{title ?? i18n.t('common:save_bucket')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onClose}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <Formik<FormValues>
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(formikProps) => (
            <form onSubmit={formikProps.handleSubmit}>
              <TextInput
                name="name"
                value={formikProps.values.name}
                label={i18n.t('common:name')}
                ionItemProps={{
                  lines: 'full',
                }}
              />
              {bucketScopeOptions.length > 1 && (
                <PickerInput
                  name="scope"
                  value={formikProps.values.scope}
                  options={bucketScopeOptions}
                  label={i18n.t('common:scratchpad_bucket_scope')}
                  ionItemProps={{
                    lines: 'none',
                  }}
                />
              )}
              <IonToolbar>
                <FormSubmitButton
                  loading={saving}
                  buttonProps={{
                    color: 'primary',
                    disabled: !formikProps.isValid || saving,
                    fill: 'solid',
                    type: 'submit',
                  }}
                >
                  {i18n.t('common:save')}
                </FormSubmitButton>
              </IonToolbar>
            </form>
          )}
        </Formik>
        <SaveStatusToast loading={saving} error={saveError} />
      </IonContent>
    </IonModal>
  );
};
